/**
 * Settings: preferences every principal has, and the sections an administrator gets.
 */

import type { RouteManifestEntry } from '../route-manifest.js';

export const SETTINGS_ROUTES: readonly RouteManifestEntry[] = [
  {
    id: 'settings',
    path: '/settings',
    entitlement: null,
    entitlementExemption:
      'Settings is where an agency configures the modules it bought, not a module it buys — ' +
      'gating it on any one key would hide the vocabulary builder from a tenant that had ' +
      'dropped that module and still had a board drawn from its labels. Every section inside ' +
      'is separately gated on the permission its own routes require.',
    /*
     * `read me`, which every role holds — and that is the point rather than an absence of gating.
     *
     * §16 is capability-scoped *by section*, not by screen: a recruiter has "personal preferences
     * only" and must still be able to reach them. A `requires` that named an administrator's
     * permission would take the whole screen away from the people whose section is the one
     * everybody has.
     */
    requires: { action: 'read', resource: 'me' },
    authenticated: true,
    nav: { labelKey: 'settings.nav', section: 'administration', order: 2 },
    load: () => import('../screens/settings.js'),
  },
  {
    /*
     * Where Google sends the browser back — M-17, 1 Sep 2026.
     *
     * A page rather than an API endpoint, so the exchange happens inside the session the person
     * already holds; `screens/meeting-connected.tsx` and `modules/pipeline/src/routes/
     * meeting-connections.ts` both carry the argument for why that is stronger than the usual
     * public callback rather than a shortcut past it.
     *
     * **This exact path is registered with Google**, matched character for character. It is no
     * longer configuration anywhere: `meetingConsentRedirectUri()` composes it from the deployment's
     * own origin, and `dev/render-env.test.ts` asserts the environment variable it used to be read
     * from is not rendered. Renaming it silently breaks every connection attempt, on the way back,
     * after consent has been given.
     */
    id: 'meeting-connected',
    path: '/settings/meetings/connected',
    /*
     * The parameters arrive from Google, so they are read defensively rather than trusted: anything
     * that is not a string is absent, and the screen has a state for absent.
     */
    validateSearch: (search) => ({
      ...(typeof search['code'] === 'string' ? { code: search['code'] } : {}),
      ...(typeof search['state'] === 'string' ? { state: search['state'] } : {}),
      ...(typeof search['error'] === 'string' ? { error: search['error'] } : {}),
    }),
    entitlement: null,
    entitlementExemption:
      'The landing page for a consent that has already been given. Gating it on the pipeline key ' +
      'would strand somebody on a blank screen holding a live authorisation code — and the act it ' +
      'performs is gated where it happens, on pipeline.meeting_connection.finish, which carries ' +
      'the entitlement and the permission.',
    /*
     * `read me`, for the same reason `/settings` uses it: this page is somebody completing an act
     * they began, and the finer questions — is this the agency's connection, may this person touch
     * it — are asked by the route, against the row, where the answer is knowable.
     */
    requires: { action: 'read', resource: 'me' },
    authenticated: true,
    /* No `nav`: nobody navigates here, Google does. */
    load: () => import('../screens/meeting-connected.js'),
  },
];
